import { useEffect } from 'react'
import {
  Routes,
  Route,
  BrowserRouter as Router,
  Outlet,
} from 'react-router-dom'
import { SnackbarProvider } from 'notistack'
import { datadogLogs } from '@datadog/browser-logs'
import CssBaseline from '@mui/material/CssBaseline'
import { makeStyles } from '@mui/styles'
import { LicenseInfo } from '@mui/x-license'
import * as Sentry from '@sentry/react'
import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import {
  createIDBPersister,
  isQueryPersisted,
  PERSISTED_TIME,
} from '@/utils/persister'
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary'
import PageTitle from '@/components/PageTitle'
import Application from './pages/Application'
import ApplicationLite from './pages/ApplicationLite'
import Authenticate from './pages/Authenticate'
import ProtectedRoutes from './pages/ProtectedRoutes'
import ZerapixThemeProvider from './theme'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})
const persister = createIDBPersister()

if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
  datadogLogs.init({
    clientToken: 'pub1ad8354d55ae38a2103b16352b97ac95',
    site: 'us5.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: 'pixydocs-web',
    env: import.meta.env.VITE_ENVIRONMENT,
    version: import.meta.env.VITE_VERSION,
  })
}

LicenseInfo.setLicenseKey(
  'e3e642adfa4c51f5def5ab3afb83d584T1JERVI6MzE4NDQsRVhQSVJZPTE2Njc0Mjc3NjEwMDAsS0VZVkVSU0lPTj0x',
)

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const useStyles = makeStyles({
  root: {
    bottom: 60,
  },
})

function isStytchAuthEnabled(): boolean {
  return !window.location.origin.includes('zerapix')
}

function MainRouter() {
  const classes = useStyles()

  useEffect(() => {
    if (!isStytchAuthEnabled()) {
      // Redirect to the correct DNS full name (stytch)
      window.location.replace(`https://${import.meta.env.VITE_DNS_FULL_NAME}`)
    }
  }, [])

  if (!isStytchAuthEnabled()) return null

  return (
    <Router>
      <SentryRoutes>
        <Route
          path="*"
          element={
            <>
              <SnackbarProvider
                autoHideDuration={2500}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                classes={{
                  containerRoot: classes.root,
                }}
              >
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </SnackbarProvider>
              <ReactQueryDevtools position="right" />
            </>
          }
        >
          <Route index element={<Authenticate />} />
          <Route path="authenticate" element={<Authenticate />} />
          <Route element={<ProtectedRoutes />}>
            <Route path="lite/*" element={<ApplicationLite />} />
            <Route path="*" element={<Application />} />
          </Route>
        </Route>
      </SentryRoutes>
    </Router>
  )
}

export default function Main() {
  // Normally react-query takes care of garbage collection, but it turns out there are rare circumstances where it doesn't.
  // This is a fall-back to take care of any inactive queries that are not being garbage collected.
  // Only remove non-persisted queries to avoid clearing cached data that should be kept long-term.
  useEffect(() => {
    setInterval(() => {
      if (queryClient.getQueryCache().getAll().length > 100) {
        queryClient.removeQueries({
          type: 'inactive',
          predicate: (query) => !isQueryPersisted(query.queryKey),
        })
      }
    }, 1000 * 60 * 30) // 30 minutes
  }, [])

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSISTED_TIME,
        // Control what gets persisted here
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            if (query.state.status !== 'success') return false
            return isQueryPersisted(query.queryKey)
          },
        },
      }}
    >
      <PageTitle>Pixydocs</PageTitle>
      <ZerapixThemeProvider>
        <CssBaseline />
        <MainRouter />
      </ZerapixThemeProvider>
    </PersistQueryClientProvider>
  )
}
